<template>
    <div>
        <form-repeater
            :repeats="repeats"
            :add="add"
            :hint="hint"
            :remove="$t('forms.auth.profile.microcopy.licenses_repeater.remove')"
            :isDisabled="isDisabled"
            :showRemoveFromIndex="numberOfSavedItems"
            @add="addRepeat()"
            @remove="removeRepeat($event)"
        >
            <template #heading="{ index }">
                {{ $t('forms.auth.profile.microcopy.licenses_repeater.heading', { index: index + 1 }) }}
            </template>

            <template #default="{ data, index }">
                <form-wrapper v-if="format === 'compact-non-mandatory'" class="form__fields" :isSubmittable="false">
                    <form-row class="fragment-row">
                        <form-cell v-if="data.id" width="half">
                            <form-info-field
                                :removeButtonText="$t('forms.professional_info.license.delete')"
                                :index="index"
                                :text="$t('forms.professional_info.licenses_repeater.input', { number: data.licenseNumber })"
                                :formRepeater="true"
                                @remove="openRemoveProfessionalInfoModal(index)"
                            ></form-info-field>
                        </form-cell>
                        <form-cell width="half">
                            <text-field
                                :label="$t('forms.auth.profile.fields.license_number.label')"
                                :placeholder="$t('forms.auth.profile.fields.license_number.placeholder')"
                                :isDisabled="isDisabled"
                                :formData="data"
                                :errorPath="valuePathField(index, 'licenseNumber')"
                                :valuePath="valuePathField(index, 'licenseNumber')"
                                :formRepeater="true"
                                @change="changeRepeat(index, $event)"
                            ></text-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell>
                            <year-field
                                :label="$t('forms.auth.profile.fields.license_issued_on.label')"
                                :placeholder="$t('forms.auth.profile.fields.license_issued_on.placeholder')"
                                :isDisabled="isDisabled"
                                :formData="data"
                                :errorPath="valuePathField(index, 'year')"
                                :valuePath="valuePathField(index, 'year')"
                                :formRepeater="true"
                                @change="changeRepeat(index, $event)"
                            ></year-field>
                        </form-cell>
                        <form-cell>
                            <select-field
                                :label="$t('forms.auth.profile.fields.license_issued_by.label')"
                                :placeholder="$t('forms.auth.profile.fields.license_issued_by.placeholder')"
                                :options="states"
                                :isDisabled="isDisabled"
                                :formData="data"
                                :errorPath="valuePathField(index, 'state')"
                                :valuePath="valuePathField(index, 'state')"
                                :formRepeater="true"
                                @change="changeRepeat(index, $event)"
                            ></select-field>
                        </form-cell>
                    </form-row>
                </form-wrapper>

                <form-wrapper v-else :isSubmittable="false">
                    <form-row>
                        <form-cell>
                            <text-field
                                :label="$t('forms.auth.profile.fields.license_number.label')"
                                :placeholder="$t('forms.auth.profile.fields.license_number.placeholder')"
                                :hasAutofocus="true"
                                :isRequired="!isDisabled"
                                :isDisabled="isDisabled"
                                :formData="data"
                                :errorPath="valuePathField(index, 'licenseNumber')"
                                :valuePath="valuePathField(index, 'licenseNumber')"
                                :formRepeater="true"
                                @change="changeRepeat(index, $event)"
                            ></text-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell>
                            <year-field
                                :label="$t('forms.auth.profile.fields.license_issued_on.label')"
                                :placeholder="$t('forms.auth.profile.fields.license_issued_on.placeholder')"
                                :isRequired="!isDisabled"
                                :isDisabled="isDisabled"
                                :formData="data"
                                :errorPath="valuePathField(index, 'year')"
                                :valuePath="valuePathField(index, 'year')"
                                :formRepeater="true"
                                @change="changeRepeat(index, $event)"
                            ></year-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell>
                            <select-field
                                :label="$t('forms.auth.profile.fields.license_issued_by.label')"
                                :placeholder="$t('forms.auth.profile.fields.license_issued_by.placeholder')"
                                :options="states"
                                :isRequired="!isDisabled"
                                :isDisabled="isDisabled"
                                :formData="data"
                                :errorPath="valuePathField(index, 'state')"
                                :valuePath="valuePathField(index, 'state')"
                                :formRepeater="true"
                                @change="changeRepeat(index, $event)"
                            ></select-field>
                        </form-cell>
                    </form-row>
                </form-wrapper>
            </template>
        </form-repeater>
        <remove-professional-info-modal
            :isOpen="isRemoveProfessionalInfoModalOpen"
            :index="indexToBeRemoved"
            name="license"
            @close="closeRemoveProfessionalInfoModal"
            @info-removed="professionalInfoRemoved"
        ></remove-professional-info-modal>
    </div>
</template>

<script>
    import FormFragmentMixin from 'mixins/FormFragmentMixin'
    import FormRepeaterMixin from 'mixins/FormRepeaterMixin'
    import RemoveProfessionalInfoModal from 'components/page-elements/profile/RemoveProfessionalInfoModal.vue'

    export default {
        mixins: [FormFragmentMixin, FormRepeaterMixin],
        components: { RemoveProfessionalInfoModal },

        props: {
            states: { type: Array, required: true },
            format: { type: String, default: 'default' },
            showRemoveProfessionalInfoModal: { type: Boolean, default: false },
            showHint: { type: Boolean, default: true },
        },
        data() {
            return {
                isRemoveProfessionalInfoModalOpen: false,
                indexToBeRemoved: 0,
            }
        },
        computed: {
            hint() {
                return this.showHint ? this.$t('forms.auth.profile.microcopy.licenses_repeater.hint') : ''
            },
            numberOfSavedItems() {
                return (this.repeats && this.repeats.filter((rep) => !!rep.id).length) || 0
            },
            add() {
                return this.format === 'compact-non-mandatory'
                    ? this.$t('forms.professional_info.licenses_repeater.add')
                    : this.$t('forms.auth.profile.microcopy.licenses_repeater.add')
            },
            heading() {
                return this.format === 'compact-non-mandatory'
                    ? this.$t('forms.professional_info.licenses_repeater.heading')
                    : this.$t('forms.auth.profile.microcopy.licenses_repeater.heading')
            },
        },
        methods: {
            openRemoveProfessionalInfoModal(index) {
                this.indexToBeRemoved = index
                this.isRemoveProfessionalInfoModalOpen = true
            },

            closeRemoveProfessionalInfoModal() {
                this.isRemoveProfessionalInfoModalOpen = false
            },
            professionalInfoRemoved(payload) {
                this.closeRemoveProfessionalInfoModal()
                this.removeRepeat(payload)
            },
        },
    }
</script>
<style scoped>
    .fragment-row {
        display: flex;
        align-items: flex-end;
        margin-top: 30px;
    }
</style>
